import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface SideRailContextValue {
  isOpen: boolean;
  initialPrompt: string;
  openSideRail: (prompt?: string) => void;
  closeSideRail: () => void;
  toggleSideRail: () => void;
  setInitialPrompt: (prompt: string) => void;
}

const SideRailContext = createContext<SideRailContextValue | undefined>(undefined);

interface SideRailProviderProps {
  children: ReactNode;
}

export function SideRailProvider({ children }: SideRailProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('');

  const openSideRail = useCallback((prompt?: string) => {
    if (prompt) {
      setInitialPrompt(prompt);
    }
    setIsOpen(true);
  }, []);

  const closeSideRail = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleSideRail = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const value: SideRailContextValue = {
    isOpen,
    initialPrompt,
    openSideRail,
    closeSideRail,
    toggleSideRail,
    setInitialPrompt,
  };

  return (
    <SideRailContext.Provider value={value}>
      {children}
    </SideRailContext.Provider>
  );
}

export function useSideRail(): SideRailContextValue {
  const context = useContext(SideRailContext);
  if (!context) {
    throw new Error('useSideRail must be used within a SideRailProvider');
  }
  return context;
}

export { SideRailContext };
